import { type IA9GridConstants } from './../grid/constants';
import { EventEmitter, AngularInjector } from '../system';

import { type IDragManager, type IDragAvatar, DragManager } from './DragManager';
import { UIListingGridV1 } from './UIListingGridV1';

interface IScope extends angular.IScope {
    grid?: any;
}

export const gridClasses = {
    placeholderBackgroundClass: 'a9GridPlaceholderBackground',
    placeholderRowClass: 'a9GridPlaceholderRow',
    gridBackgroundClass: 'listing-grid-v1__content',
    placeholderHoverClass: 'a9GridPlaceholderHover'
};

type TCancelMoveCallback = (data: any) => boolean | Promise<boolean>;

const RECEIVED = 'received';
const REMOVED = 'removed';
const CANCELED = 'cancelled';
const DROPPED = 'dropped';

export class GridDragManager extends EventEmitter {
    protected dragManager: IDragManager = null;
    protected gridListeners: GridDragManager[] = [];
    protected $scope: IScope = null;
    protected elementSelector = '.ui-grid-row';
    protected clones = [];
    protected startRows = [];
    protected startRowIndex = -1;
    protected gridApi = null;
    protected moveStep = 0;

    // private static placeholderBackgroundClass = 'a9GridPlaceholderBackground';
    // private static placeholderRowClass = 'a9GridPlaceholderRow';
    // private static gridBackgroundClass = 'listing-grid-v1__content';

    private moveCheckCallback: (data) => boolean = null;
    private cancelMoveCallback: TCancelMoveCallback | null = null;

    constructor(protected id: string) {
        super();
        this.setDragManager(new DragManager(`[id="${this.id}"] ${this.elementSelector}`));
    }

    public get RECEIVED() {
        return RECEIVED;
    }

    public get REMOVED() {
        return REMOVED;
    }

    public get CANCELED() {
        return CANCELED;
    }

    public get DROPPED() {
        return DROPPED;
    }

    public disable() {
        this.dragManager?.stop();
    }

    public activate() {
        this.dragManager?.run();
    }

    /*
    public static swipe(array, current, previous, data) {

        if (current === UIListingGridV1.GRID_LAST_ROW) {
            array.push(data);
            array.splice(previous, 1);
        } else if (current >= 0) {
            if (current >= previous) {
                array.splice(current, 0, data);
                array.splice(previous, 1);
            } else {
                array.splice(current, 0, data);
                array.splice(previous + 1, 1);
            }
        }
    }
    */

    public setMoveCheckCallback(callback) {
        this.moveCheckCallback = callback;
    }

    /**
     * Set an optional cancel move callback (can be async) to prevent an item being moved from the source grid to the destination grid.
     */
    public setCancelMoveCallback(callback: TCancelMoveCallback | null) {
        this.cancelMoveCallback = callback;
    }

    public dragScroll(avatar) {
        if (this.gridApi && this.gridApi.scrollableGrid) {
            const grid = UIListingGridV1.getGrid(this.id);
            const step = this.getMoveStep(avatar, grid);

            if (step == 0 && this.moveStep != 0) {
                // stop scroll
                this.gridApi.scrollableGrid.raise.stopDragScroll();
            }
            if (step < 0) {
                if (this.moveStep >= 0) {
                    // start scroll top
                    this.gridApi.scrollableGrid.raise.startDragScroll(-1);
                } else if (step > this.moveStep) {
                    // stop scroll
                    this.gridApi.scrollableGrid.raise.stopDragScroll();
                }
            }
            if (step > 0) {
                if (this.moveStep <= 0) {
                    // start scroll bottom
                    this.gridApi.scrollableGrid.raise.startDragScroll(1);
                } else if (step < this.moveStep) {
                    // stop scroll
                    this.gridApi.scrollableGrid.raise.stopDragScroll();
                }
            }
            this.moveStep = step;
        }
    }

    private getMoveStep(element: HTMLElement, parent: HTMLElement) {
        if (element && parent) {
            const rect1 = element.getBoundingClientRect();
            const rect2 = parent.getBoundingClientRect();
            const over = (Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left)) / rect2.width;

            if (over > 0.1) {
                return rect1.bottom < rect2.top ? rect1.bottom - rect2.top : rect1.top > rect2.bottom ? rect1.top - rect2.bottom : 0;
            }
        }

        return 0;
    }

    public addGridListeners(...listeners: GridDragManager[]) {
        listeners.forEach(listner => {
            this.addGridListener(listner);
        });
    }

    public addGridListener(listener: GridDragManager) {
        if (listener && !this.hasGridListener(listener)) {
            this.gridListeners.push(listener);
        }
    }

    public removeGridListener(listener: GridDragManager) {
        if (this.hasGridListener(listener)) {
            this.gridListeners.splice(this.gridListeners.indexOf(listener), 1);
        }
    }

    public hasGridListener(listener: GridDragManager) {
        return !!this.gridListeners.find(item => item === listener);
    }

    public setScope(scope) {
        this.$scope = scope;
        this.$scope.$on('$destroy', () => {
            if (this.dragManager) {
                this.dragManager.destroy();
            }
        });
    }

    public setGridApi(api) {
        this.gridApi = api;
    }

    public onGridReady() {
        UIListingGridV1.clearLastRow(this.id, this.elementSelector);
    }

    public reset(rows) {
        UIListingGridV1.removeRows(rows);
        UIListingGridV1.sortIndexes(UIListingGridV1.getGrid(this.id));
    }

    public hasAvatar(avatar) {
        return this.getAvatarRow(avatar) != UIListingGridV1.GRID_OUTSIDE;
    }

    public getAvatarRow(avatar) {
        return UIListingGridV1.getRowIndexUnderElement(this.id, this.elementSelector, avatar, this.getRowHeight());
    }

    public destroy() {
        this.dragManager && this.dragManager.destroy();
    }

    public getDroppedInfo(
        avatar,
        data: Record<string, any>[],
        diff: number = 1
    ): Promise<{
        indexes: {
            first: number;
            previous: number;
            current: number;
        };
        data: any;
        rows: any[];
        currentRowIndex: number;
    }> {
        const currentRowIndex = this.getAvatarRow(avatar);

        if (typeof currentRowIndex !== 'boolean') {
            let current = currentRowIndex;

            if (this.placeToTheSameRow(currentRowIndex) && !currentRowIndex) {
                current = 1;
            }

            const indexes = this.parseIndexes(avatar, current);

            if (indexes.previous === current - diff) {
                return Promise.resolve(null);
            }

            const rows = this.findGridRows(indexes.current),
                coords = DragManager.getCoords(rows[0]);

            if (indexes.current === UIListingGridV1.GRID_LAST_ROW) {
                coords.top += this.getRowHeight();
            }

            if (!this.canMoveRows({ indexes: indexes, data: data[indexes.first] })) {
                this.cancelled(avatar, data, true);
                return Promise.resolve(null);
            }

            return avatar
                .apply(coords)
                .then(() => {
                    return {
                        indexes: indexes,
                        data: data[indexes.first],
                        rows: rows,
                        currentRowIndex: currentRowIndex
                    };
                })
                .catch(error => {
                    throw error;
                });
        }

        return Promise.resolve(null);
    }

    protected dragStarted(avatar: IDragAvatar, data: any[], mine: boolean) {
        this.moveStep = 0;
        if (mine) {
            this.startRowIndex = UIListingGridV1.getRowId(avatar);
            this.startRows = this.findGridRows(this.startRowIndex);
        }
    }

    protected dragProcess(avatar: IDragAvatar, data: any[], mine: boolean) {
        if (this.hasAvatar(avatar)) {
            const currentRowIndex = this.getAvatarRow(avatar);
            if (currentRowIndex === UIListingGridV1.GRID_LAST_ROW) {
                this.insertRowsAfterIndex(currentRowIndex, mine);
            } else {
                this.insertRowsBeforeIndex(currentRowIndex, mine);
            }
        } else {
            this.adjustGridRowsHeights();
        }

        this.dragScroll(avatar);
    }

    protected dragStopped(avatar: IDragAvatar, data: any[], mine: boolean) {
        if (mine) {
            if (!avatar.busy) {
                avatar.busy = true;
                if (this.hasAvatar(avatar)) {
                    // I's caught that
                    this.dropped(avatar, data);
                } else {
                    // nobody caught that
                    this.cancelled(avatar, data, mine);
                }
            } else {
                // someone has caught that
                this.finishRemove(avatar, data, mine);
            }
        } else {
            const currentRowIndex = this.getAvatarRow(avatar);
            const indexes = {
                first: currentRowIndex,
                previous: currentRowIndex,
                current: currentRowIndex
            };
            if (
                !avatar.busy &&
                this.hasAvatar(avatar) &&
                (currentRowIndex < 0 ||
                    this.canMoveRows({
                        indexes: indexes,
                        data: data[indexes.first]
                    }))
            ) {
                avatar.busy = true;
                this.received(avatar, data);
            }
        }
    }

    protected insertRowsAfterIndex(index, _mine) {
        const currentRows = this.findGridRows(index);
        const contentPlace = UIListingGridV1.getGrid(this.id).closest(`.${gridClasses.gridBackgroundClass}`);
        this.adjustGridRowsHeights();
        this.hideRows(this.startRows);
        contentPlace.classList.add(gridClasses.placeholderBackgroundClass);
    }

    protected insertRowsBeforeIndex(index, mine) {
        this.adjustGridRowsHeights();
        this.hideRows(this.startRows);
        if (index === this.startRowIndex && mine) {
            index++;
        }
        const height = this.getRowHeight();
        const currentRows = this.findGridRows(index);
        currentRows.forEach(row => {
            const placeholder: HTMLElement = row.querySelector(`.${gridClasses.placeholderRowClass}`);
            placeholder.hidden = false;
            if (this.startRowIndex === index) {
                row && (row.style.height = `${height}px`) && (row.style.overflow = 'hidden');
            } else {
                row && (row.style.height = `${height * 2}px`);
            }
        });
    }

    protected parseIndexes(element, currentIndex = -1) {
        const previous = UIListingGridV1.getRowId(element);
        const first = (UIListingGridV1.getRowOldId(element) > -1 && UIListingGridV1.getRowOldId(element)) || previous;
        return {
            first: first,
            previous: previous,
            current: currentIndex
        };
    }

    protected cancelled(avatar, data, mine) {
        const indexes = this.parseIndexes(avatar, this.startRowIndex);
        this.insertRowsBeforeIndex(indexes.current, mine);
        avatar.rollback().finally(() => {
            this.cancel(indexes, data[indexes.first]);
        });
    }

    protected cancel(indexes, datum) {
        this.showRows(this.startRows);
        this.adjustGridRowsHeights();
        this.startRows = [];
        this.startRowIndex = -1;
        this.emit(this.CANCELED, {
            indexes: indexes,
            data: datum
        });
    }

    protected canMoveRows(request) {
        return !this.moveCheckCallback || this.moveCheckCallback(request);
    }

    protected async finishRemove(avatar: IDragAvatar, data: any[], mine: boolean): Promise<void> {
        avatar.cancelMovePromise = this.cancelMove(avatar, data);

        const shouldCancel = await avatar.cancelMovePromise;

        delete avatar.cancelMovePromise;

        if (shouldCancel) {
            this.cancelled(avatar, data, mine);
        } else {
            this.removed(avatar, data);
        }
    }

    protected dropped(avatar, data, diff?: number) {
        this.getDroppedInfo(avatar, data, diff).then(result => {
            if (result) {
                if (result.currentRowIndex !== UIListingGridV1.GRID_LAST_ROW) {
                    if (!this.placeToTheSameRow(result.currentRowIndex)) {
                        UIListingGridV1.replaceBefore(this.startRows, result.rows);
                    }
                } else {
                    UIListingGridV1.replaceAfter(this.startRows, result.rows);
                }

                this.showRows(this.startRows);
                this.adjustGridRowsHeights();
                this.startRows = [];
                this.startRowIndex = -1;
                UIListingGridV1.sortIndexes(UIListingGridV1.getGrid(this.id));

                this.emit(this.DROPPED, {
                    indexes: result.indexes,
                    data: result.data
                });
            }
        });
    }

    protected getData() {
        let data = [];
        if (this.$scope && this.$scope.grid && this.$scope.grid.items && this.$scope.grid.items.length > 0) {
            data = this.$scope.grid.items;
        }
        return data;
    }

    protected removed(avatar, data) {
        const initialId = UIListingGridV1.getRowId(this.startRows[0]);
        const indexes = this.parseIndexes(avatar, initialId);
        this.adjustGridRowsHeights();
        this.startRows = [];
        const rows = this.findGridRows(indexes.current);
        this.showRows(rows);
        UIListingGridV1.removeRows(rows);
        UIListingGridV1.sortIndexes(UIListingGridV1.getGrid(this.id));
        this.startRowIndex = -1;
        this.emit(this.REMOVED, {
            indexes: indexes,
            data: data[indexes.first],
            rows: rows
        });
    }

    protected findGridRows(current) {
        return UIListingGridV1.findGridRows(UIListingGridV1.getGrid(this.id), this.elementSelector, current);
    }

    protected async cancelMove(avatar: IDragAvatar, data: any[]): Promise<boolean> {
        if (!this.cancelMoveCallback) {
            return false;
        }

        const initialId = UIListingGridV1.getRowId(this.startRows[0]);
        const indexes = this.parseIndexes(avatar, initialId);
        const datum = data[indexes.first];

        try {
            return !!(await this.cancelMoveCallback(datum));
        } catch {
            return true;
        }
    }

    protected received(avatar, data) {
        const current = this.getAvatarRow(avatar);
        if (typeof current !== 'boolean') {
            const indexes = this.parseIndexes(avatar, current);
            const rows = this.findGridRows(current);
            let coords = DragManager.getCoords(UIListingGridV1.getGrid(this.id).closest('.listing-grid-v1__content'));
            if (rows.length > 0) {
                coords = DragManager.getCoords(rows[0]);
                if (indexes.current === UIListingGridV1.GRID_LAST_ROW) {
                    coords.top += this.getRowHeight();
                }
            }
            const startRows = UIListingGridV1.findGridRows(avatar, this.elementSelector);
            avatar.apply(coords).then(async () => {
                const shouldCancelMove = await (avatar.cancelMovePromise || Promise.resolve(false));

                if (shouldCancelMove) {
                    this.adjustGridRowsHeights();
                    return;
                }

                rows.forEach((row, key) => {
                    const avatarCells = UIListingGridV1.getRowCells(startRows[key]);
                    UIListingGridV1.getRowCells(row).forEach((cell, k) => {
                        avatarCells[k] && (avatarCells[k].style.width = `${cell.offsetWidth}px`);
                    });
                });
                //document.body.removeChild(avatar);
                if (current !== UIListingGridV1.GRID_LAST_ROW) {
                    UIListingGridV1.replaceBefore(startRows, rows);
                } else {
                    UIListingGridV1.replaceAfter(startRows, rows);
                }
                this.startRows = [];
                this.adjustGridRowsHeights();
                this.startRowIndex = UIListingGridV1.GRID_OUTSIDE;
                UIListingGridV1.sortIndexes(UIListingGridV1.getGrid(this.id));
                this.emit(this.RECEIVED, {
                    indexes: indexes,
                    data: data[indexes.first],
                    rows: startRows
                });
            });
        }
    }

    protected createAvatarFunction() {
        this.dragManager.setAvatarFunction(e => {
            const container = document.body;
            let existingAvatar;
            while ((existingAvatar = container.querySelector('.grid-avatar-row'))) {
                container.removeChild(existingAvatar);
            }
            const row = UIListingGridV1.getClosestRow(this.id, this.elementSelector, <HTMLElement>e.target);
            let avatar: IDragAvatar = null;
            if (this.isDraggableRow(row)) {
                avatar = <IDragAvatar>UIListingGridV1.createAvatarElement(this.id, this.elementSelector, <HTMLElement>e.target);
            }
            return <IDragAvatar>avatar;
        });
    }

    protected hideRows(rows) {
        rows.forEach(row => {
            row.hidden = true;
        });
    }

    protected showRows(rows) {
        rows.forEach(row => {
            row.hidden = false;
        });
    }

    protected adjustGridRowsHeights() {
        const othersRows = UIListingGridV1.findGridRows(UIListingGridV1.getGrid(this.id), this.elementSelector);
        const height = this.getRowHeight();

        othersRows.forEach(row => {
            row && (row.style.height = `${height}px`) && (row.style.overflow = '');
            const placeholder: HTMLElement = row.querySelector(`.${gridClasses.placeholderRowClass}`);
            placeholder.hidden = true;
        });
        const contentPlace = UIListingGridV1.getGrid(this.id).closest('.listing-grid-v1__content');
        contentPlace.classList.remove(gridClasses.placeholderBackgroundClass);
    }

    protected isDraggableRow(row: HTMLElement) {
        const rowIndex = UIListingGridV1.getRowId(row);
        const findGridRows = UIListingGridV1.findGridRows(UIListingGridV1.getGrid(this.id), this.elementSelector, rowIndex);
        let isDraggable = false;
        findGridRows.forEach(row => {
            const result = row.querySelector('[on-drag]');
            if (result) {
                isDraggable = true;
            }
        });
        return isDraggable;
    }

    protected placeToTheSameRow(currentRowIndex) {
        return currentRowIndex === this.startRowIndex || currentRowIndex - 1 === this.startRowIndex;
    }

    private getRowHeight(): number {
        if (this.$scope.grid && this.$scope.grid.options && this.$scope.grid.options.rowHeight) {
            return this.$scope.grid.options.rowHeight;
        }

        return AngularInjector.get<IA9GridConstants>('a9GridConstants').dataRowHeight;
    }

    private setDragManager(dragManager: IDragManager) {
        this.dragManager = dragManager;
        this.dragManager.on('prestart', () => {
            UIListingGridV1.sortIndexes(UIListingGridV1.getGrid(this.id));
        });
        this.dragManager.on('start', avatar => {
            this.dragStarted(avatar, this.getData(), true);
            this.gridListeners.forEach(listener => {
                listener.dragStarted(avatar, this.getData(), false);
            });
        });
        this.dragManager.on('move', avatar => {
            this.dragProcess(avatar, this.getData(), true);
            this.gridListeners.forEach(listener => {
                listener.dragProcess(avatar, this.getData(), false);
            });
        });
        this.dragManager.on('finish', avatar => {
            this.gridListeners.forEach(listener => {
                listener.dragStopped(avatar, this.getData(), false);
            });
            this.dragStopped(avatar, this.getData(), true);
        });
        this.createAvatarFunction();
    }
}
